import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { Sheet, SheetContent, SheetTitle } from '@goaltree/ui/components/sheet'
import { useQuery } from 'convex/react'
import { ArrowLeft, BellOff, Leaf, Settings, Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { GroveChat } from '@/components/groves/grove-chat'
import { GroveIcon } from '@/components/groves/grove-icon'
import { type GroveDetail, GroveOverview, GroveStreak } from '@/components/groves/grove-overview'
import { GroveSettingsSheet } from '@/components/groves/grove-settings-sheet'
import { UserAvatar } from '@/components/user-avatar'

const iconBtn =
  'flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'

// A grove's chat shown in place of the list on Today's Groves tab.
export function GroveInline({ groveId, onBack }: { groveId: string; onBack: () => void }) {
  const { t } = useTranslation()
  const grove = useQuery(api.groves.get, { groveId: groveId as Id<'groves'> })
  const [membersOpen, setMembersOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 border-b border-border/50 px-4 py-2">
        <div className="mx-auto flex max-w-(--page-col) items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            aria-label={t('common.back')}
            className={`${iconBtn} -ml-2`}
          >
            <ArrowLeft className="size-5" />
          </button>
          {grove && <GroveIcon icon={grove.icon} size="sm" />}
          <p className="min-w-0 flex-1 truncate text-base font-bold text-foreground">
            {grove?.name ?? ''}
          </p>
          {grove?.me.muted && <BellOff className="size-3.5 shrink-0 text-muted-foreground" />}
          {grove && (
            <>
              <button
                type="button"
                onClick={() => setMembersOpen(true)}
                aria-label={t('groves.membersTitle')}
                className={iconBtn}
              >
                <Users className="size-[18px]" />
              </button>
              <button
                type="button"
                onClick={() => setSettingsOpen(true)}
                aria-label={t('groves.settings.title')}
                className={iconBtn}
              >
                <Settings className="size-[18px]" />
              </button>
            </>
          )}
        </div>
      </div>
      {grove === null ? (
        <NotFound onBack={onBack} />
      ) : grove ? (
        <>
          <MembersStrip grove={grove} onOpen={() => setMembersOpen(true)} />
          <GroveChat
            groveId={grove._id}
            isOwner={grove.me.role === 'owner'}
            coachVoice={grove.coachVoice}
          />
          <Sheet open={membersOpen} onOpenChange={setMembersOpen}>
            <SheetContent
              side="right"
              showCloseButton={false}
              className="flex w-full flex-col overflow-y-auto p-5 sm:max-w-sm"
            >
              <SheetTitle className="sr-only">{grove.name}</SheetTitle>
              <GroveOverview grove={grove} />
            </SheetContent>
          </Sheet>
          <GroveSettingsSheet grove={grove} open={settingsOpen} onOpenChange={setSettingsOpen} />
        </>
      ) : (
        <div className="flex-1" />
      )}
    </div>
  )
}

function MembersStrip({ grove, onOpen }: { grove: GroveDetail; onOpen: () => void }) {
  const { t } = useTranslation()
  const requests = useQuery(
    api.groveShowcase.listRequests,
    grove.me.role === 'owner' ? { groveId: grove._id } : 'skip',
  )
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full shrink-0 cursor-pointer border-b border-border/50 px-4 py-2 text-left"
    >
      <div className="mx-auto flex max-w-(--page-col) items-center gap-3">
        <div className="flex -space-x-2">
          {grove.members.slice(0, 5).map((m) => (
            <UserAvatar
              key={m.userId}
              name={m.name}
              image={m.image}
              className={`size-7 rounded-full border-2 object-cover ${m.checkedInToday ? 'border-primary' : 'border-background'}`}
            />
          ))}
        </div>
        <p className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          {grove.allInToday
            ? t('groves.today.allIn')
            : t('groves.today.checkedIn', {
                done: grove.checkedInToday,
                total: grove.memberCount,
              })}
        </p>
        {!!requests?.length && (
          <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-[11px] font-bold text-primary-foreground">
            {t('groves.requests.badge', { count: requests.length })}
          </span>
        )}
        <GroveStreak grove={grove} compact />
      </div>
    </button>
  )
}

function NotFound({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
      <Leaf className="size-10 text-muted-foreground" strokeWidth={1.75} />
      <p className="text-lg font-bold text-foreground">{t('groves.notFound.title')}</p>
      <p className="text-sm text-muted-foreground">{t('groves.notFound.desc')}</p>
      <button
        type="button"
        onClick={onBack}
        className="mt-2 cursor-pointer text-sm font-semibold text-primary-text underline-offset-4 hover:underline"
      >
        {t('groves.notFound.back')}
      </button>
    </div>
  )
}
